<template>
  <div class="min-h-screen">
    <div class="flex flex-col h-screen overflow-hidden bg-growth-25">
      <Top_Nav_Bar v-if="currentLayout !== 'bare' && currentLayout !== 'external'" />

      <!-- Content area with sidebar -->
      <div :class="['flex flex-col md:flex-row flex-1 overflow-hidden', isAuthenticated ? 'rounded-t-xl border-t border-gray-200' : '']">
        <!-- Main App Navigation Sidebar (always visible) -->
        <MainNavSidebar v-if="isAuthenticated && currentLayout !== 'bare' && currentLayout !== 'external'" />

        <!-- Main content area. .fic-capture-target marks the region the
             Focus Item capture widget screenshots (content only, not chrome). -->
        <div class="flex-1 overflow-auto bg-white h-full fic-capture-target">
          <router-view />
        </div>
      </div>
    </div>

    <!-- App-wide Focus Item capture overlay (admins only; self-gated). -->
    <FocusItemCapture v-if="isAuthenticated && !isDemoMode" />

    <AnnouncementModal v-if="currentLayout !== 'bare' && !isDemoMode" ref="announcementModal" />
    <AnnouncementEditor v-if="current_user?.is_superuser && currentLayout !== 'bare' && !isDemoMode" />

    <!-- Persistent floating phone call UI — only visible during active calls -->
    <PhoneCallFloater v-if="isAuthenticated" />

    <!-- Demo Mode presence tell: a small square in the top-right corner that is
         only rendered while spoofing is active. Intentionally unlabeled and
         visually innocuous so a prospect reads it as a UI dot, while the
         presenter knows it confirms Demo Mode is ON. If it's gone, spoofing is
         OFF -- do not screen-share. -->
    <div v-if="isDemoMode" class="demo-mode-tell" aria-hidden="true" />
  </div>
</template>

<script>
import Top_Nav_Bar from '@/components/navigation/Top_Nav_Bar.vue';
import MainNavSidebar from '@/components/navigation/MainNavSidebar.vue';
import AnnouncementModal from '@/components/AnnouncementModal.vue';
import AnnouncementEditor from '@/components/AnnouncementEditor.vue';
import FocusItemCapture from '@/components/FocusItemCapture.vue';
import PhoneCallFloater from '@/components/PhoneCallFloater.vue';
import { mapGetters } from 'vuex';
import * as Sentry from '@sentry/vue';
import { activateSpoof, deactivateSpoof } from '@/utils/demoSpoof';
import { canAccessFeature } from '@/utils/access';
import { FEATURES } from '@/constants/features';
import {
  bootPylonWidget,
  shutdownPylonWidget,
  hidePylonWidget,
  showPylonWidget,
} from '@/services/pylonWidget';
import { twilioDevice } from '@/services/TwilioDeviceService';

// Read the persisted Demo Mode flag without ever throwing. If storage is
// unreachable we cannot prove Demo Mode is OFF, so fail closed (true) rather
// than crash the app on mount.
//
// NOTE: failing closed here is more impactful than in main.js. There it only
// suppresses session replay; here `demoMode = true` makes mounted() spoof/blur
// the entire app, so a normal user whose storage *reads* throw (e.g. "block all
// cookies", locked-down browser) boots into a blurred, unusable app -- not just
// lost telemetry. We accept this: a blurred app is recoverable, a PHI leak in a
// demo is not. (In practice this is rare and near-impossible to hit legitimately
// -- if reads throw, writes throw too, so such a browser can never have persisted
// 'true' in the first place; only a transient read failure can reach this path.)
function readDemoModeFlag() {
  try {
    return localStorage.getItem('demoMode') === 'true';
  } catch {
    return true;
  }
}

export default {
  name: 'App',
  components: {
    FocusItemCapture,
    PhoneCallFloater,
    Top_Nav_Bar,
    MainNavSidebar,
    AnnouncementModal,
    AnnouncementEditor,
  },
  data() {
    return {
      // Fail closed if storage is unreachable, consistent with main.js and
      // demoSpoof.isDemoMode() -- and never let a storage exception crash mount.
      demoMode: readDemoModeFlag(),
      pylonBooted: false,
    };
  },
  computed: {
    ...mapGetters('auth', ['current_user', 'isAuthenticated']),
    ...mapGetters('tenant', ['current_tenant_slug', 'current_tenant_name']),
    currentLayout() {
      return this.$route.meta.layout
    },
    isDemoMode() {
      return this.demoMode;
    },
  },
  watch: {
    demoMode(val) {
      document.body.classList.toggle('demo-mode', val);
      try {
        localStorage.setItem('demoMode', val);
      } catch {
        /* storage unavailable -- the in-session toggle still applies */
      }
      if (val) {
        // Stop Sentry session replay so the live (and any pre-spoof) DOM is no
        // longer shipped to a third party. Replay cannot be restarted in-session;
        // a full off->on->off cycle requires a reload to resume capture, which is
        // the safe direction. Best-effort: ignore if replay isn't running.
        try {
          Sentry.getReplay()?.stop();
        } catch {
          /* replay not active */
        }
        this.$nextTick(() => activateSpoof());
        if (this.pylonBooted) hidePylonWidget();
      } else {
        deactivateSpoof();
        if (this.pylonBooted && this.currentLayout !== 'bare') showPylonWidget();
      }
    },

    current_user: {
      immediate: true,
      handler(user) {
        if (user && canAccessFeature(FEATURES.WIDGET_PYLON)) {
          bootPylonWidget(user).then(() => {
            this.pylonBooted = true;
            if (this.isDemoMode || this.currentLayout === 'bare') hidePylonWidget();
          });
        } else if (!user && this.pylonBooted) {
          this.pylonBooted = false;
          shutdownPylonWidget();
        }

        if (!user && twilioDevice.isInitialized) {
          twilioDevice.destroy();
        }
      },
    },

    currentLayout(layout) {
      if (!this.pylonBooted || this.isDemoMode) return;
      if (layout === 'bare') {
        hidePylonWidget();
      } else {
        showPylonWidget();
      }
    },
  },
  mounted() {
    document.body.classList.toggle('demo-mode', this.demoMode);
    if (this.demoMode) {
      this.$nextTick(() => activateSpoof());
    }
    // Handle the case where the user lands directly on a route with a modal
    // (the watcher won't fire for the initial route, only subsequent navigations)
    this.$nextTick(() => {
      this.$refs.announcementModal?.checkModal(this.$route);
    });
  },
  methods: {},
};
</script>

<style>
#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
}

/* Growth module design tokens (CSS custom properties) */
:root {
  /* Semantic grays */
  --growth-900: #1E1F21;
  --growth-800: #353537;
  --growth-700: #78797A;
  --growth-600: #626264;
  --growth-50: #E8E9E9;
  --growth-25: #F4F4F4;
  --growth-10: #FAFAFA;

  /* Primary accent */
  --growth-tangerine: #F76734;
  --growth-tangerine-hover: #E55A2B;

  /* Semantic status */
  --growth-info: #7799C2;
  --growth-success: #166534;
  --growth-warning: #92400E;
  --growth-error: #D53D3D;

  /* Shadows */
  --growth-shadow-sm: 0 1px 2px rgba(0,0,0,0.05);
  --growth-shadow-md: 0 4px 12px rgba(0,0,0,0.08);
  --growth-shadow-lg: 0 8px 24px rgba(0,0,0,0.12);

  /* Tag category backgrounds (10-15% opacity) */
  --growth-tag-aco-bg: rgba(100, 116, 139, 0.15);
  --growth-tag-aco-text: #334155;
  --growth-tag-volume-bg: rgba(217, 119, 6, 0.12);
  --growth-tag-volume-text: #92400E;
  --growth-tag-type-bg: rgba(37, 99, 235, 0.10);
  --growth-tag-type-text: #1E40AF;
  --growth-tag-population-bg: rgba(124, 58, 237, 0.10);
  --growth-tag-population-text: #5B21B6;
  --growth-tag-setting-bg: rgba(8, 145, 178, 0.10);
  --growth-tag-setting-text: #0E7490;
  --growth-tag-tech-bg: rgba(107, 114, 128, 0.12);
  --growth-tag-tech-text: #374151;
}

/* Demo mode: prevent selecting/copying sensitive data that hasn't been spoofed yet */
body.demo-mode .sensitive {
  user-select: none;
}

/* Belt-and-suspenders: hide Pylon launcher in demo mode (programmatic hide also applied) */
body.demo-mode #pylon-launcher,
body.demo-mode #pylon-launcher-frame {
  display: none !important;
}

/* Fail-closed kill switch: if the spoof engine fails to initialize we cannot
   prove anything is masked, so blur the entire app rather than leak real data. */
body.demo-mode-failed {
  filter: blur(16px) !important;
}

/* Demo Mode presence tell -- see template. Small, muted, corner-pinned square.
   Reads as an innocuous UI accent to anyone who doesn't know what it is, but
   gives the presenter positive confirmation that spoofing is active. Pinned
   above everything and non-interactive so it never blocks clicks. */
.demo-mode-tell {
  position: fixed;
  top: 4px;
  right: 4px;
  width: 8px;
  height: 8px;
  border-radius: 2px;
  background-color: #c8d2e0;
  opacity: 0.55;
  z-index: 2147483647;
  pointer-events: none;
}
</style>
